import { useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { api, jsonBody } from "../../api/client";
import { useAuth } from "../../auth/AuthProvider";
import { membershipAllows } from "../../auth/permissions";
import type { TenantBrand } from "../../theme/tenantBrand";
import { Card, CardHeader, ErrorPanel, Field, FormActions, Notice } from "../../ui/components";
import { PanelSkeleton } from "../../ui/skeletons";

interface Tenant {
  id: string;
  slug: string;
  legalName: string;
  displayName: string;
  timezone: string;
  locale: string;
  currency: string;
  status: string;
  dataRegion: string;
  onboardingState: string;
  brand: TenantBrand;
  version: number;
}

export function OrganizationSettings() {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const tenantId = auth.session?.selectedMembership?.tenantId;
  const tenant = useQuery({
    queryKey: ["tenant", tenantId],
    queryFn: () => api<Tenant>("/api/v1/tenant"),
    enabled: Boolean(tenantId),
  });
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<unknown>();
  const [saved, setSaved] = useState(false);
  const canManage = membershipAllows(auth.session?.selectedMembership, "tenant.manage");

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!tenant.data || !canManage) return;
    const form = new FormData(event.currentTarget);
    setWorking(true);
    setError(undefined);
    setSaved(false);
    try {
      await api<Tenant>("/api/v1/tenant", {
        method: "PUT",
        body: jsonBody({
          displayName: String(form.get("displayName")),
          timezone: String(form.get("timezone")),
          locale: String(form.get("locale")),
          brand: {
            logoUrl: String(form.get("logoUrl")),
          },
          version: tenant.data.version,
        }),
      });
      await Promise.all([
        tenant.refetch(),
        queryClient.invalidateQueries({ queryKey: ["auth", "session"] }),
      ]);
      setSaved(true);
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  if (tenant.isLoading) return <PanelSkeleton table={false} />;
  if (tenant.error) return <ErrorPanel error={tenant.error} />;
  if (!tenant.data) return null;

  return (
    <div className="content-grid">
      <Card className="span-8">
        <CardHeader
          title="Organization identity"
          description="Organization identity used across events, documents, email, and reporting. Interface colors are managed by the product for consistency and accessibility."
        />
        {error ? <ErrorPanel error={error} /> : null}
        {saved ? <Notice tone="success">Organization settings saved.</Notice> : null}
        {!canManage ? (
          <Notice tone="warning">Only the tenant owner can change these settings.</Notice>
        ) : null}
        <form className="form-grid" onSubmit={save}>
          <Field label="Display name">
            <input
              name="displayName"
              defaultValue={tenant.data.displayName}
              required
              minLength={2}
              disabled={!canManage}
            />
          </Field>
          <Field
            label="Legal name"
            hint="The legal name is controlled by platform support after verification."
          >
            <input value={tenant.data.legalName} readOnly />
          </Field>
          <Field label="Timezone" hint="Use an IANA timezone such as America/New_York.">
            <input
              name="timezone"
              defaultValue={tenant.data.timezone}
              required
              disabled={!canManage}
            />
          </Field>
          <Field label="Locale">
            <input name="locale" defaultValue={tenant.data.locale} required disabled={!canManage} />
          </Field>
          <Field label="Currency">
            <input value={tenant.data.currency} readOnly />
          </Field>
          <Field label="Data region">
            <input value={tenant.data.dataRegion} readOnly />
          </Field>
          <Field
            label="Logo URL"
            hint="Use an HTTPS image URL. Your logo appears alongside the product-owned interface theme."
          >
            <input
              name="logoUrl"
              type="url"
              defaultValue={tenant.data.brand.logoUrl}
              placeholder="https://assets.example.com/logo.svg"
              disabled={!canManage}
            />
          </Field>
          {canManage ? (
            <div className="full">
              <FormActions submitting={working} submitLabel="Save organization" />
            </div>
          ) : null}
        </form>
      </Card>
      <Card className="span-4">
        <CardHeader title="Tenant record" description="Stable identifiers and account state." />
        <dl className="definition-list">
          <dt>Tenant slug</dt>
          <dd className="mono">{tenant.data.slug}</dd>
          <dt>Status</dt>
          <dd>{tenant.data.status}</dd>
          <dt>Onboarding</dt>
          <dd>{tenant.data.onboardingState}</dd>
          <dt>Version</dt>
          <dd>{tenant.data.version}</dd>
        </dl>
        <Notice tone="info" title="Regional deployment">
          The data region is an operational setting. Moving regions requires a planned migration and
          customer notice.
        </Notice>
      </Card>
    </div>
  );
}
